const toPositiveInteger = (value, fallback = 1) => {
  const parsedValue = Number(value);
  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
};

export const createEmptyDashboardCell = () => ({
  biReportId: null,
  width: 1,
  height: 1,
});

export const getDashboardCellLayout = (cells = [], columns = 1, rows = 1) => {
  const columnCount = toPositiveInteger(columns);
  const rowCount = toPositiveInteger(rows);
  const cellCount = columnCount * rowCount;
  const occupiedCells = new Set();

  return Array.from({ length: cellCount }, (_, index) => {
    const row = Math.floor(index / columnCount) + 1;
    const column = (index % columnCount) + 1;

    if (occupiedCells.has(index)) {
      return { index, row, column, covered: true, width: 1, height: 1 };
    }

    const cell = cells[index] ?? createEmptyDashboardCell();
    const width = Math.min(toPositiveInteger(cell.width), columnCount - column + 1);
    const height = Math.min(toPositiveInteger(cell.height), rowCount - row + 1);

    for (let rowOffset = 0; rowOffset < height; rowOffset += 1) {
      for (let columnOffset = 0; columnOffset < width; columnOffset += 1) {
        if (rowOffset === 0 && columnOffset === 0) continue;
        occupiedCells.add(index + rowOffset * columnCount + columnOffset);
      }
    }

    return { index, row, column, covered: false, width, height };
  });
};

const findFirstAvailablePosition = (occupiedCells, columns, rows, width, height) => {
  for (let row = 0; row <= rows - height; row += 1) {
    for (let column = 0; column <= columns - width; column += 1) {
      const indexes = [];

      for (let rowOffset = 0; rowOffset < height; rowOffset += 1) {
        for (let columnOffset = 0; columnOffset < width; columnOffset += 1) {
          indexes.push((row + rowOffset) * columns + column + columnOffset);
        }
      }

      if (indexes.every((index) => !occupiedCells.has(index))) return indexes;
    }
  }

  return null;
};

export const expandDashboardReports = (reports = [], columns = 1, rows = 1) => {
  const columnCount = toPositiveInteger(columns);
  const rowCount = toPositiveInteger(rows);
  const cells = Array.from({ length: columnCount * rowCount }, createEmptyDashboardCell);
  const occupiedCells = new Set();

  reports.forEach((report) => {
    const width = Math.min(toPositiveInteger(report.width), columnCount);
    const height = Math.min(toPositiveInteger(report.height), rowCount);
    const indexes = findFirstAvailablePosition(occupiedCells, columnCount, rowCount, width, height);

    if (!indexes) return;

    cells[indexes[0]] = { ...report, width, height };
    indexes.forEach((index) => occupiedCells.add(index));
  });

  return cells;
};

export const compactDashboardReports = (cells = [], columns = 1, rows = 1) =>
  getDashboardCellLayout(cells, columns, rows)
    .filter(({ covered }) => !covered)
    .map(({ index, width, height }) => ({
      biReportId: cells[index]?.biReportId ?? null,
      width,
      height,
    }));
